import { useEffect, useRef } from "react"
import { Search, Scale, PenLine, FileText } from "lucide-react"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useAssistantStore } from "@/stores/assistant-store"
import { MessageBubble } from "@/components/assistant/MessageBubble"
import { ActivityPhases } from "@/components/chat/ActivityPhases"
import { StarterList, type Starter } from "@/components/chat/StarterList"
import { useAuthStore } from "@/stores/auth-store"
import { greetUser } from "@/lib/greeting"

const ASSISTANT_STARTERS: Starter[] = [
  { text: "Which safety accreditations do we hold, and when do they expire?", icon: Search },
  { text: "Compare how we described mobilisation across recent bids", icon: Scale },
  { text: "Draft a capability summary from the library", icon: PenLine },
  { text: "Find past answers about subcontractor management", icon: FileText },
]

export function ChatMessageArea() {
  const { messages, isStreaming, activityPhases, sendMessage } = useAssistantStore()
  const undoMemorySaved = useAssistantStore((s) => s.undoMemorySaved)
  const fullName = useAuthStore((s) => s.user?.user_metadata?.["full_name"] as string | undefined)
  const scrollRef = useRef<HTMLDivElement>(null)

  const handleUndoMemory = (messageId: string, memoryId: string) => {
    void undoMemorySaved(messageId, memoryId)
  }

  const hasPhases = activityPhases.phases.length > 0

  // Auto-scroll to bottom on new messages or activity changes
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages.length, isStreaming, activityPhases])

  // Empty state when there are no messages
  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center text-center">
        <h3 className="text-[28px] [@media(max-height:720px)]:text-2xl font-medium tracking-tight leading-tight mb-5 text-foreground">
          {greetUser(fullName)}
        </h3>
        <p className="text-base text-muted-foreground max-w-md mb-6">
          Research, compare and draft from everything your company has written.
        </p>
        <StarterList starters={ASSISTANT_STARTERS} onSelect={(text) => void sendMessage(text)} />
      </div>
    )
  }

  return (
    <ScrollArea className="h-full" ref={scrollRef}>
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} onUndoMemory={handleUndoMemory} />
        ))}
        {hasPhases && (
          <div>
            <ActivityPhases phases={activityPhases} isStreaming={isStreaming} />
          </div>
        )}
      </div>
    </ScrollArea>
  )
}
